/**
 * Resolución de safe-area insets para el host web/mobile.
 *
 * Contexto: con `cordova-android 15` + `targetSdkVersion 36`, Android 15+ fuerza edge-to-edge
 * y `env(safe-area-inset-*)` funciona correctamente. En Android <= 14 el WebView NO es
 * edge-to-edge fiable: el sistema ya reserva las barras y `env(safe-area-inset-*)` puede
 * devolver valores incorrectos, provocando dobles márgenes o headers cortados.
 *
 * Estrategia: en Android <= 14 forzamos el inset a 0 (custom override). En el resto
 * (Android 15+, iOS, desktop) devolvemos `undefined` para que los estilos usen `env()`.
 */

import { isAndroid } from "react-device-detect";

/** Versión a partir de la cual `env(safe-area-inset-*)` es fiable en Android (edge-to-edge real). */
const ANDROID_RELIABLE_EDGE_TO_EDGE_MAJOR = 15;

/**
 * Devuelve la versión mayor de Android del WebView actual, o `null` si no es Android.
 *
 * @returns {number|null}
 */
export function getAndroidMajorVersion() {
    if (typeof navigator === "undefined" || !navigator.userAgent) {
        return null;
    }
    const match = navigator.userAgent.match(/Android\s+(\d+)/i);
    return match ? parseInt(match[1], 10) : null;
}

/**
 * Indica si el WebView corre en Android con una versión sin edge-to-edge fiable (<= 14).
 *
 * @returns {boolean}
 */
export function isLegacyAndroidInset() {
    const major = getAndroidMajorVersion();
    return major !== null && major < ANDROID_RELIABLE_EDGE_TO_EDGE_MAJOR;
}

/**
 * Override de safe-area insets a inyectar en el `ThemeProvider2` de cada layout.
 * `0` fuerza a ignorar `env()` (Android <= 14); `undefined` deja que el estilo use `env()`.
 *
 * @returns {{ top: (number|undefined), bottom: (number|undefined) }}
 */
export function getCustomSafeAreaInsets() {
    if (isLegacyAndroidInset()) {
        return { top: 0, bottom: 0 };
    }
    if (isAndroid && window.cordova) {
        return { top: 48, bottom: 24 };
    }
    return { top: undefined, bottom: undefined };
}
